// /opt/isp_erp/frontend/src/components/warehouse/materiales/AlertasStockPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Grid, Alert, CircularProgress, TablePagination, FormControl, InputLabel, Select,
  MenuItem, FormControlLabel, Switch, LinearProgress
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { warehouseAPI } from '../../../api/warehouse.api';
import { Kpi, CampoBusqueda, SelectProveedor, detalleError, fmtNum, fechaHora, useDebouncedValue } from './utils';

const AlertasStockPanel = ({ tecnicos, proveedores }) => {
  const [texto, setTexto] = useState('');
  const busqueda = useDebouncedValue(texto.trim(), 400);
  const [tecnicoId, setTecnicoId] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [agotados, setAgotados] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, page_size: rowsPerPage, incluir_agotados: agotados };
      if (busqueda) params.q = busqueda;
      if (tecnicoId !== '') params.tecnico_id = tecnicoId;
      if (proveedorId !== '') params.proveedor_id = proveedorId;
      const r = await warehouseAPI.alertasStockPaginado(params);
      setData(r.data);
    } catch (err) {
      setData({ items: [], total: 0 });
      setError(detalleError(err, 'Error al cargar las alertas'));
    } finally {
      setLoading(false);
    }
  }, [busqueda, tecnicoId, proveedorId, agotados, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Alertas de stock bajo</Typography>
          <Typography variant="body2" color="text.secondary">
            Técnicos que tienen de un material menos que su mínimo, de lo más crítico a lo menos.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <Kpi
              titulo="Alertas activas"
              valor={data.total}
              detalle="Según los filtros aplicados"
              color={data.total > 0 ? 'warning.main' : 'inherit'}
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <CampoBusqueda value={texto} onChange={setTexto} label="Buscar material, código o técnico" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Técnico</InputLabel>
                <Select label="Técnico" value={tecnicoId} onChange={(e) => { setTecnicoId(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(tecnicos || []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.username || t.nombre || t.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <SelectProveedor value={proveedorId} proveedores={proveedores} onChange={(e) => { setProveedorId(e.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={agotados} onChange={(e) => { setAgotados(e.target.checked); setPage(0); }} />}
                label="Incluir técnicos con stock agotado (0)"
              />
            </Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {!loading && !error && data.total === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>No hay alertas de stock bajo con estos filtros</Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Material</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Técnico</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 200 }}>Stock actual y mínimo</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Faltante</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Última actualización</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              ) : data.items.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center">Sin alertas</TableCell></TableRow>
              ) : (
                data.items.map((a) => (
                  <TableRow key={a.alerta_id} hover>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="body2" fontWeight="bold">{a.material}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {a.codigo} · {a.proveedor || 'Sin proveedor'}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.tecnico_nombre || `Técnico ${a.tecnico_id}`}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        <strong>{fmtNum(a.stock_actual)}</strong> de {fmtNum(a.stock_minimo)} {a.unidad}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, a.porcentaje)}
                        color={a.porcentaje <= 25 ? 'error' : 'warning'}
                        sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <strong>{fmtNum(a.faltante)}</strong> {a.unidad}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fechaHora(a.fecha_alerta)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, nueva) => setPage(nueva)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </CardContent>
    </Card>
  );
};

export default AlertasStockPanel;
